import { GRADE_COLORS, gradeFromScore } from '@/lib/radar/scoring'

/**
 * Label / value / hairline meter — the sub-metric row inside a category card
 * and the severity row in the issues panel.
 *
 * A null value renders an EMPTY track and an em-dash, never a zero-width bar
 * at grade F. "No data" and "scored zero" have to look different, because they
 * mean different things.
 */
export function MeterRow({
  label,
  value,
  /** Denominator for the fill width. Defaults to a 0-100 score scale. */
  max = 100,
  /** Bar colour. Defaults to the grade colour derived from `value`. */
  color,
  /** Track thickness. The design uses 3px in cards, 4px in the issues panel. */
  thickness = 3,
  /** Rendered instead of the raw number (e.g. a count rather than a score). */
  display,
}: {
  label: string
  value: number | null
  max?: number
  color?: string
  thickness?: number
  display?: string
}) {
  const hasValue = value !== null
  const barColor =
    color ?? (hasValue ? GRADE_COLORS[gradeFromScore(value)] : undefined)
  // Guard the denominator: max=0 would produce NaN%.
  const pct =
    hasValue && max > 0 ? Math.max(0, Math.min(100, (value / max) * 100)) : 0

  return (
    <div className='flex flex-col gap-[5px]'>
      <div className='flex items-baseline justify-between gap-2 text-xs'>
        <span
          className={hasValue ? 'text-muted-foreground' : 'text-instrument'}
        >
          {label}
        </span>
        <span
          className={`font-mono tabular-nums ${hasValue ? 'text-muted-foreground' : 'text-instrument'}`}
        >
          {display ?? (hasValue ? value : '—')}
        </span>
      </div>
      <div className='w-full bg-surface-inset' style={{ height: thickness }}>
        {hasValue && pct > 0 && (
          <div
            className='transition-[width] duration-500'
            style={{
              height: thickness,
              width: `${pct}%`,
              background: barColor,
            }}
          />
        )}
      </div>
    </div>
  )
}
